import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FunnelContentProvider } from './FunnelContentContext';
// WEB-1988: прелоадер — публичный (без Ant Design). Визард рендерится и в
// воронке покупателя, и в превью конструктора; кит-прелоадер тянет в бандл
// покупателя весь antd.
import { PublicPreloader as Preloader } from '@/components/public/PublicPreloader';
import { PublicBlockRenderer } from '@/components/editor/PublicBlockRenderer';
import { microFeedbackRevealProps } from '@/player/microFeedbackReveal';
import { blocksFollowingExplicitSpacing } from '@/components/editor/blockSpacing';
import { useEditorPreview } from '@/components/editor/EditorPreviewContext';
import { getStylePackById } from '@/themes/stylePacks';
import { useFormValues } from '@/components/editor/FormValuesContext';
import type { EditorBlock } from '@/store/editorSlice';
import { safeHref } from '@/utils/isAllowedUrl';
import { isMeaningfulScreenName } from '@/utils/screenName';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { playerKitDoor } from '@/player/playerKitDoor';
import { getSessionId } from '@/utils/analytics/sessionId';
// WEB-2124 / WEB-2164: единая дверь ухода наружу и сборка адреса серверной
// точки ухода. Провайдер двери подаёт ТОЛЬКО плеер квиза — вне его (канва
// конструктора, пейволл) блоки уводят сами, как раньше.
import {
  ExternalExitProvider,
  type ExternalExitContextValue,
} from '@/components/editor/externalExitContext';
import {
  buildQuizExitUrl,
  resolveExitVisitorFields,
} from '@/utils/quizExitUrl';
import { getStoredUtm } from '@/utils/analytics/utmCapture';
import { quizOutboundMacroValues } from './quizOutboundMacros';
// WEB-2262: перевод значений плеера в имена меток для кнопки-ссылки.
import { toOutboundMacroValues } from '@/utils/outboundUrlMacros';
import { relayFunnelEventToBridge } from '@/utils/web2appBridge';
import { flushAnalyticsQueue } from '@/utils/analytics/eventQueue';
import type { EvalContext } from '@/logic-engine';
import { resolveScreenRefs } from '@/logic-engine/resolveScreenRefs';
import { collectAnswerUnits } from '@/logic-engine/answerUnits';
import { HiddenByRuleMark } from './HiddenByRuleMark';
import { decideTransition } from '@/pages/navigation/navigationStateMachine';
import { dispatchQuizTransition } from './quizTransitionDispatch';
import { useHistoryStack } from '@/pages/navigation/historyStack';
import type { VariableContext } from '@/player/variable-context';
import { collectNamedVariables } from '@/player/variable-context';
import { collectMeasureSources } from '@/player/measureSources';
import { resolveVisibility } from '@/player/visibility';
import { readDomSources, buildSyncSources } from '@/player/sources';
import { needsPaymentOnScreen0 } from '@/player/sources/needs-payment-on-screen0';
import {
  fireMetaBrowserEvent,
  resolvePlayerConsent,
} from '@/utils/analytics/playerPixelEvents';
import { ScreenBackgroundWrapper } from '@/components/paywall-runtime/ScreenBackgroundWrapper';
import { resolveScreenLayout } from '@/components/screen-layout/resolveScreenLayout';
import {
  screenChromeLayoutStyle,
  screenRootLayoutStyle,
} from '@/components/screen-layout/screenLayoutStyles';
import { ScreenContentColumn } from '@/components/screen-layout/ScreenContentColumn';
import { arrangeScreenContent } from './QuizContentCard';
import { findEmailValue } from './emailLookup';
import {
  loadQuizProgress,
  saveQuizProgress,
  type PendingLeadSnapshot,
} from './quizProgressStorage';
import { trackQuizEvent } from './trackQuizEvent';
import {
  singleImageChoicePick,
  collectScreenAnswers,
  resolvePickerUnitDefault,
} from './answerCollection';
import { resolveFunnelSensitiveConsent } from './sensitiveConsent';
import {
  findScreenBlockers,
  type ScreenBlockerReason,
} from './requiredGate';
import { useAutoAdvance } from './useAutoAdvance';
import { answersWithoutScreen, clearScreenValues, useSkipAdvance } from './skipAdvance';
import { dockUnlessSkipOnly } from '@/components/editor/skipLink';
import { resolveAdvanceDelayMs } from './autoAdvanceDelay';
// Пачка настроек 2 (повтор Slimkit): шапка по разделам, прибитые шапка и
// кнопка, «вперёд», лого на десктопе, пауза перехода по кнопкам, сброс
// прокрутки. Логика — в отдельных модулях, здесь только вызовы.
// WEB-3447: рамка экрана Slimkit (шапка, шевроны, прибитая кнопка, пауза
// перехода) — ленивым куском через дверь; пакам без рамки — запасной набор.
import { useQuizChromeKit } from './quizChromeKitDoor';
import { usePrefetchNextScreenImages } from './usePrefetchNextScreenImages';
import type { QuizScreenWizardProps } from './quizScreenWizardProps';
// WEB-3402: части плеера, вынесенные из этого файла без изменений поведения.
import { keepFieldFocusOnPress } from './keepFieldFocusOnPress';
import { collectQuizBlockMeta } from './quizBlockMeta';
import { useQuizPaymentProbe } from './useQuizPaymentProbe';
import { useQuizVisitorPath } from './useQuizVisitorPath';
import { useQuizShownStep } from './quizShownStep';
import { useQuizScreenEvents } from './useQuizScreenEvents';
import { createQuizLeadDelivery, useQuizRestoredLeadRetry } from './useQuizLeadDelivery';
import { renderQuizCompletedStep } from './QuizCompletedStep';
import { renderQuizFallbackCta } from './QuizFallbackCta';
import { renderQuizBackSlot } from './QuizBackSlot';
import { QuizLogoRow, QuizStepCounter } from './QuizHeaderRow';
import { QuizHeaderProgressBars, quizHeaderShowsProgress } from './QuizHeaderProgress';
import { headerBackKind, headerCounterTotal } from './quizHeaderScreen';
import * as logoOnly from './QuizLogoOnlyChrome';
import {
  QUIZ_HEADER_LOGO_DEFAULT_HEIGHT,
  progressNodeBox,
  readQuizHeader,
  resolveQuizHeader,
} from './quizHeader';
import { quizHeaderInsetStyle } from './quizHeaderInset';

export type { QuizScreenWizardProps };


/**
 * QuizScreenWizard — the quiz player.
 *
 * Wires F1b decideTransition + historyStack into the quiz player. Same
 * 4-tier chain as PaywallScreenWizard. History-based Back button.
 *
 * WEB-848: extracted from PublicQuiz.tsx into a reusable module (mirrors
 * WEB-662 atom 1's paywall-runtime extraction) so the editor preview can
 * render through the same wizard instead of a separate flat-block
 * re-implementation. Behavior-preserving move for the public/preview page —
 * the only NEW behavior is the isPreview data-block-id wrapping (AC7) and
 * gating captureLeadPublic on isPreview (AC6, see submitEmailIfPresent).
 */
/** Положение логотипа шапки → выравнивание строки (слово владельца 21.10.2026). */

export function QuizScreenWizard({
  screens,
  logic: logicRaw,
  geoCountry,
  language,
  quizSlug,
  quizId,
  projectId,
  textColor,
  showProgress,
  stylePackId,
  logoUrl,
  quizHeader,
  quizCta,
  quizLoader,
  buttonStyle,
  isPreview,
  funnel,
  startAtPaywall,
  controlledIndex,
  servedStale,
  contentVersion,
  outboundAnswersBlocked,
}: QuizScreenWizardProps) {
  // WEB-780: сетка безопасности для уже сохранённых сломанных правил — AI-путь
  // раньше писал scope_ref/target именем экрана ("Screen 1"), плеер матчит по
  // id. Приводим ссылки к id один раз при загрузке; неразрешимые остаются как
  // есть (движок логики fail-open). Новые правила уже приходят с id (BE-фикс).
  const logic = useMemo(
    () => resolveScreenRefs(logicRaw, screens),
    [logicRaw, screens],
  );
  // WEB-2177: прогресс из ПРЕДЫДУЩЕГО визита этого браузера — читается РОВНО
  // один раз, до первого рендера (лениво, как и положено useState-инициализатору
  // — React гарантирует один вызов на инстанс компонента, повторный рендер его
  // не повторяет). `isPreview` (черновик `?preview=1`, канва редактора —
  // PhonePreview всегда передаёт isPreview) исключён намеренно: черновик может
  // держать тот же versionId, что и последняя публикация, но другое
  // содержимое — восстановление сюда увело бы автора в чужой прогресс
  // покупателя (и наоборот, тестовые клики автора не должны попасть в общий с
  // покупателями ключ). `quizProgressStorage` сам не восстановит ничего без
  // contentVersion — второй слой защиты того же риска.
  //
  // ЖАЛОБА ПОКУПАТЕЛЯ 18.09.2026 (funnel.quizsomaapp.info): четвёртым
  // параметром уезжает «страница открыта ШТАТНЫМ шагом оплаты». Без него
  // восстановленный `isCompleted` открывал экран оплаты по чистой рекламной
  // ссылке и запирал квиз на сутки, обходя двойную защиту WEB-1740. Значение —
  // тот же самый проп `startAtPaywall`, который PublicQuiz.tsx считает ОДНОЙ
  // константой и для чтения прогресса, и для этого пропа: второго источника
  // правды быть не должно.
  //
  // Признак — ОДНА константа, а не выражение прямо в аргументе: сторож
  // `quizProgressPaywallStep.guard.test.ts` судит это по всем вызовам чтения,
  // потому что выражение на месте и есть второй источник правды (находка 1
  // прохода 2 свежего взгляда).
  const atPaywallStep = startAtPaywall === true;
  const [restoredProgress] = useState(() =>
    isPreview
      ? undefined
      : loadQuizProgress(projectId, quizId, contentVersion, { atPaywallStep }),
  );
  const [currentIndex, setCurrentIndex] = useState(() => {
    const idx = restoredProgress?.screenIndex;
    return typeof idx === 'number' && idx >= 0 && idx < screens.length
      ? idx
      : 0;
  });
  // WEB-848 atom (Вариант B): the editor's selection wins over the wizard's
  // own currentIndex state; internal navigation still runs (see handleNext/
  // handleBack below) but stops short of calling setCurrentIndex/setIsCompleted.
  const isControlled = controlledIndex !== undefined;
  const displayIndex = isControlled ? controlledIndex : currentIndex;
  const [isCompleted, setIsCompleted] = useState(
    startAtPaywall === true || restoredProgress?.isCompleted === true,
  );
  // WEB-2177 (находка 4, проход 5) — ДВА РАЗНЫХ ФАКТА, которые до этой правки
  // были одним:
  //   · `emailCaptured` — «почту с этого экрана мы уже ОТПРАВИЛИ»: гасит
  //     повторную отправку той же почты внутри сессии (в том числе пока запрос
  //     в полёте). Восстановленная незавершённая попытка (`pendingLead`) тоже
  //     означает «отправляли»: добивать её надо повтором из ref, а не второй
  //     отправкой с экрана;
  //   · `emailDelivered` — «сервер лид ПРИНЯЛ». Именно этот факт уезжает в
  //     хранилище: иначе перезагрузка после отказа сервера восстанавливала бы
  //     «почту забрали», повторы не запускались, и контакт терялся навсегда.
  //
  // НАХОДКА 2 СВЕЖЕГО ВЗГЛЯДА (18.09): от восстановленной недоотправки признак
  // поднимается ТОЛЬКО когда это продолжение прежнего прохода. Сброшенный
  // завершённый проход (`restartedAfterCompletion`, жалоба 18.09) — новый
  // проход: почта в нём ещё не отправлялась, и гасить её отправку старой
  // недоотправкой нельзя. Иначе покупатель вводит почту заново, а отправка
  // молча выходит — ни лида, ни события, ни письма; при исчерпанных повторах
  // старой недоотправки терялись бы ОБА контакта. Само добивание старой
  // недоотправки при этом остаётся — оно идёт на монтировании, из ref.
  const [emailCaptured, setEmailCaptured] = useState(
    restoredProgress?.emailCaptured === true ||
      (restoredProgress?.pendingLead != null &&
        restoredProgress.restartedAfterCompletion !== true),
  );
  const [emailDelivered, setEmailDelivered] = useState(
    restoredProgress?.emailCaptured === true,
  );
  // Зеркало `pendingLeadRef` в состоянии: ref читается синхронно (повтор), а
  // сохранение прогресса идёт эффектом — эффекту нужен повод пересчитаться.
  const [pendingLead, setPendingLeadState] =
    useState<PendingLeadSnapshot | null>(restoredProgress?.pendingLead ?? null);
  const rememberPendingLead = (next: PendingLeadSnapshot | null): void => {
    pendingLeadRef.current = next;
    setPendingLeadState(next);
  };
  // F1b: variables state for RESTART reset (D5: RESTART clears ANSWER + VARIABLE).
  const [variables, setVariables] = useState<Record<string, string>>(
    () => restoredProgress?.variables ?? {},
  );
  const { values, resetValues, setValue } = useFormValues();
  // F1b: history stack + cycle-guard counter.
  // Жалоба покупателя 18.09: стек поднимается из сохранённого прогресса — без
  // этого после перезагрузки внутри квиза история пуста, экран считается
  // первым (`isFirst` ниже) и кнопка «Назад» пропадает. Объявлен ЗДЕСЬ, а не
  // ниже по файлу, потому что история уезжает в сохраняемый снимок — эффект
  // сохранения стоит следующим.
  const historyStack = useHistoryStack(screens, restoredProgress?.history);
  // Отдельной переменной: она же стоит в списке зависимостей сейв-эффекта.
  // Массив в стеке пересоздаётся на каждом продвижении и на каждом «Назад»,
  // поэтому эффект действительно пересчитывается, а не замирает на старой
  // истории.
  const historyEntries = historyStack.history;
  // Жалоба покупателя 18.09, третий член того же класса: отметки сработавшего
  // авто-перехода (`экран:блок`). Состояние живёт ЗДЕСЬ, а не внутри
  // useAutoAdvance, по той же причине, что и история: его кладёт в снимок
  // эффект сохранения, который стоит следующим, а сам узел авто-перехода
  // объявлен много ниже (правила хуков — он обязан стоять выше раннего выхода
  // по проверке способа оплаты, но ниже этого места).
  const [autoAdvancedBlocks, setAutoAdvancedBlocks] = useState<string[]>(
    () => restoredProgress?.autoAdvanced ?? [],
  );
  const rememberAutoAdvanced = useCallback((blockKey: string) => {
    setAutoAdvancedBlocks((prev) =>
      prev.includes(blockKey) ? prev : [...prev, blockKey],
    );
  }, []);
  // WEB-2177 (находка 1 свежего взгляда, проход 3): в сохранение уходят
  // РЕАЛЬНЫЕ ответы — `values` из FormValuesContext, а НЕ `variables` (это
  // F1b VARIABLE-стейт: он в этом визарде никогда не заполняется настоящими
  // ответами, единственный вызов setVariables ниже по файлу — сброс в {} на
  // RESTART). До этой правки сюда уходил именно `variables`, то есть в
  // хранилище всегда лежал пустой объект — после перезагрузки восстанавливались
  // номер экрана и флаги, а ответы покупателя терялись, и ветвление/подстановки
  // на следующих экранах (результаты, пейволл) считали, что человек ничего не
  // отвечал. `variables` сохраняем как раньше — он часть контракта
  // QuizProgressSnapshot и участвует в EvalContext.variables.
  //
  // Дёшево (несколько строк JSON, синхронный localStorage) и не требует ловли
  // закрытия вкладки/сворачивания на телефоне, у которых нет надёжного
  // unload-события. Ничего денежного здесь нет (см. докблок
  // quizProgressStorage.ts) — цену/тариф/оплату всегда считает заново сервер.
  // Какой ввод покупателя НЕ уезжает в браузер (решение владельца 17.09.2026:
  // почта, телефон, вес и рост) — ОДНИМ местом в quizProgressStorage.ts
  // (SENSITIVE_INPUT_BLOCK_TYPES). Отсюда туда едет состав воронки: ключ в
  // `values` — это id блока, свой у каждой воронки, и списком ключей решение
  // не выразить. Параметр обязательный — забыть его не даст компилятор.
  useEffect(() => {
    if (isPreview) return;
    saveQuizProgress(
      projectId,
      quizId,
      contentVersion,
      {
        screenIndex: currentIndex,
        // Жалоба покупателя 18.09: номера пройденных экранов — чтобы «Назад»
        // пережил перезагрузку. Личных данных здесь нет, только числа.
        history: historyEntries,
        // Жалоба 18.09: отметки сработавшего авто-перехода — без ответов, одни
        // идентификаторы экрана и блока (см. докблок поля в хранилище).
        autoAdvanced: autoAdvancedBlocks,
        variables,
        values,
        isCompleted,
        emailCaptured: emailDelivered,
        ...(pendingLead ? { pendingLead } : {}),
      },
      screens,
    );
  }, [
    isPreview,
    projectId,
    quizId,
    contentVersion,
    currentIndex,
    historyEntries,
    // Счётчик переходов: продвижение вперёд на ТОТ ЖЕ номер экрана (цикл,
    // GOTO на себя) историю удлиняет, а `currentIndex` не меняет — без этого
    // повода эффект такое продвижение проспал бы.
    historyStack.transitionCount,
    autoAdvancedBlocks,
    variables,
    values,
    isCompleted,
    emailDelivered,
    pendingLead,
    screens,
  ]);
  // WEB-1025 (часть 2, возврат QA 09.08): isPreview НЕ отличает конструктор
  // от боя — PublicQuiz.tsx (настоящая публичная страница) сам выставляет
  // isPreview=true для режима предпросмотра черновика (`?preview=1`,
  // WEB-411). isEditorPreview — единственный сигнал «я внутри канвы
  // редактора» (PhonePreview.tsx оборачивает визард в EditorPreviewProvider
  // isEditorPreview, WEB-681/683); публичная страница им не оборачивает
  // никогда, ни в бою, ни в предпросмотре черновика.
  const { isEditorPreview } = useEditorPreview();
  // Логотип при скрытой шапке — только на телефоне (решение владельца 23.10).
  const desktopViewport = logoOnly.useDesktopViewport(isEditorPreview);
  // WEB-895: обязательные незаполненные блоки, по которым уже была попытка
  // продвинуться — им рисуем warning. Сбрасывается при заполнении/смене экрана.
  const [requiredWarnIds, setRequiredWarnIds] = useState<Set<string>>(
    new Set(),
  );
  // WEB-1716: лид, чья отправка провалилась (сервер лежит / сети нет). Помним
  // payload целиком — экран с полем почты к моменту повтора уже позади.
  // WEB-2177 (находка 4, проход 5): начальное значение — из восстановленного
  // снимка. Не доехавший лид теперь переживает перезагрузку вкладки, а не
  // умирает вместе с её памятью.
  const pendingLeadRef = useRef<PendingLeadSnapshot | null>(
    restoredProgress?.pendingLead ?? null,
  );
  // WEB-2177 / WEB-1716: добивание ВОССТАНОВЛЕННОЙ недоотправки на
  // монтировании — см. useQuizLeadDelivery.ts. Ссылку на повтор плеер
  // заполняет ниже раннего возврата рендера (`retryPendingLeadRef.current`).
  const retryPendingLeadRef = useQuizRestoredLeadRetry(
    isPreview,
    pendingLeadRef,
    restoredProgress?.pendingLead == null,
  );
  // Per-mount guards. Fire-once events live in a Set keyed by event name;
  // repeatable per-screen events live in a Set keyed by `EVENT:screenId`.
  const oneShot = useRef<Set<string>>(new Set());
  const perScreen = useRef<Set<string>>(new Set());
  // WEB-2524: последнее ОТПРАВЛЕННОЕ значение ответа по паре «экран + блок».
  const lastSentAnswer = useRef<Map<string, string>>(new Map());
  // Починка ревизии («два быстрых тапа Далее»): переход экрана планируется
  // через startTransition — прерываемо, а не мгновенно. Второй тап в окне
  // между «сорвался переход» и «новый экран закоммичен» раньше попадал в
  // handleNext уже НОВОГО экрана (handleNextRef переписывался прямо в
  // рендере, до коммита) — либо, если оба тапа легли в один и тот же
  // синхронный батч, оба раза выполнялся handleNext ОДНОГО и того же (ещё
  // старого) экрана, и historyStack.push срабатывал дважды на один и тот же
  // индекс («Назад» показывался на первом экране, хотя возвращаться уже
  // некуда). Флаг — обычный ref (не React-состояние): его значение видно
  // ЛЮБОМУ замыканию handleNext сразу же, в момент чтения, а не только на
  // следующем рендере — поэтому защёлка держит и разнесённый по задачам
  // рендер/коммит, и слипшиеся в один батч клики. Снимается эффектом ниже,
  // когда currentIndex реально сменился (см. useEffect после stepPosition).
  const transitionInFlightRef = useRef(false);

  // WEB-610: preview traffic (?preview=1, unpublished quiz) never has a
  // backing /public/quiz/.../event endpoint on the backend — sending it 404s
  // on every screen transition and spams Sentry. `track` is the single choke
  // point every analytics event in this component must go through so preview
  // is suppressed uniformly, matching the QUIZ_VIEW/pixel gate already applied
  // one level up in PublicQuizPage.
  const track = (event: string, payload: Record<string, unknown> = {}) => {
    if (isPreview) return;
    trackQuizEvent(projectId, quizSlug, event, payload);
    // WEB-1143 (П4): тот же поток событий ретранслируется в WebView-мост, когда
    // квиз открыт внутри контейнера SDK (no-op в обычном браузере). PII-фильтр —
    // внутри relayFunnelEventToBridge (email/сырой ответ не уходят).
    relayFunnelEventToBridge(event, payload);
  };


  // F1d: sync UA detection — computed once on mount from navigator.userAgent.
  // readDomSources isolates the DOM read so tests can stub navigator.userAgent
  // before rendering and get the correct value via useState initializer.
  const [syncSources] = useState(() => {
    // WEB-1368 п.6.19 (проход 4 свежего взгляда по пачке 41): адрес разбирается
    // ОДИН раз здесь; varCtx ниже берёт syncSources.urlParams.
    const { ua, search } = readDomSources();
    return buildSyncSources({ search, ua, geoCountry: null, variables: {} });
  });

  // F1d: async payment method probe — lazy, runs only when PAYMENT_METHOD
  // condition exists in logic (см. useQuizPaymentProbe.ts).
  const [paymentMethod, paymentMethodReady] = useQuizPaymentProbe(
    logic,
    isPreview,
  );

  // F6: build stable blockOptions + multi-choice id set from all screens.
  // Recomputed only when screens prop changes (quiz data loaded/swapped).
  const [multiChoiceBlockIds, blockOptions, blockMeta] = useMemo(
    () => collectQuizBlockMeta(screens),
    [screens],
  );

  // F6: VariableContext for {{interpolation}} — rebuilt when answers or screens change.
  const varCtx = useMemo<VariableContext>(() => {
    // Build answers: split comma-joined multiple_choice values into string[].
    const answers: Record<string, string | string[]> = {};
    for (const [id, val] of Object.entries(values)) {
      if (!val) continue;
      answers[id] = multiChoiceBlockIds.has(id) ? val.split(',').filter(Boolean) : val;
    }
    // URL params — fail-open in test/SSR environments without window.location.
    return {
      answers,
      urlParams: syncSources.urlParams,
      system: { geoCountry: geoCountry ?? null, locale: language },
      blockOptions,
      // WEB-182: per-block type/label for format-by-type interpolation.
      blockMeta,
      // WEB-115: variableName → blockId registry so {{myVar}} resolves to the
      // bound block's answer at runtime (editor binds it, persisted in content).
      namedVariables: collectNamedVariables(screens),
      // Решение владельца №5: какие пикеры дают рост и вес для {{bmi}} и др.
      measureSources: collectMeasureSources(screens),
    };
  }, [values, multiChoiceBlockIds, blockOptions, blockMeta, geoCountry, language, screens, syncSources]);

  // F1c: reactive visibility — recomputed on every values/screen/logic change.
  // EvalContext.answers includes ALL values (incl. hidden blocks) per A4 contract:
  // hiding a block must NOT remove its answer so other conditions can still use it.
  // F1d: uses real UA-detected deviceOs/browser/browserInApp and async paymentMethod.
  //
  // Проход по швам пачки 45 (С2): снимок условий вынесен из хука скрытых
  // блоков — тот же снимок нужен, чтобы посчитать видимость блоков ЧУЖОГО
  // (уже пройденного) экрана, см. `funnelSensitiveConsent`.
  // WEB-2350: единицы ответов «Веса» и «Роста» — правило сравнивает в своих.
  const answerUnits = useMemo(() => collectAnswerUnits(screens, values, resolvePickerUnitDefault), [screens, values]);
  const evalCtx = useMemo<EvalContext>(
    () => ({
      // Reuse varCtx's answer shaping (multi_select → string[]) so visibility
      // rules see arrays, not comma-joined strings (multi_select is_one_of/
      // contains operators need the array LHS). A4: includes ALL values incl.
      // hidden blocks (varCtx iterates every non-empty value).
      answers: varCtx.answers,
      variables,
      urlParams: varCtx.urlParams,
      geoCountry: geoCountry ?? null,
      deviceOs: syncSources.deviceOs,        // F1d: real UA-detected OS
      browser: syncSources.browser,           // F1d: real UA-detected browser
      browserInApp: syncSources.browserInApp, // F1d: real in-app detection
      paymentMethod,                           // F1d: async Stripe probe result
      answerUnits,
    }),
    [varCtx, variables, geoCountry, syncSources, paymentMethod, answerUnits],
  );

  const hiddenSet = useMemo<Set<string>>(() => {
    // WEB-848: safeIndex (not the raw currentIndex state) — in controlled
    // mode currentIndex never moves, but the screen actually rendered (and
    // its visibility rules) must track controlledIndex.
    const si = Math.min(displayIndex, Math.max(screens.length - 1, 0));
    const screen = screens[si];
    if (!screen) return new Set<string>();
    return resolveVisibility(screen.blocks, logic, evalCtx);
  }, [displayIndex, screens, logic, evalCtx]);

  // WEB-848: displayIndex-derived safeIndex so the rendered screen always
  // matches the editor's selection in controlled mode (see hiddenSet above
  // for the same substitution).
  const safeIndex = Math.min(displayIndex, Math.max(screens.length - 1, 0));
  const currentScreen = screens[safeIndex];
  // F1b: isFirst = no back history (not index-based — correct after GOTO jumps).
  const isFirst = historyStack.history.length === 0;
  const isLast = safeIndex === screens.length - 1;

  // WEB-1995: пока покупатель стоит на этом экране — тихо подгружаем картинки
  // экранов, куда он может уйти одним действием (см. usePrefetchNextScreenImages).
  usePrefetchNextScreenImages({
    screens,
    currentIndex: safeIndex,
    logic,
    varCtx,
    variables,
    geoCountry,
    syncSources,
    paymentMethod,
  });

  // WEB-1812: честный прогресс по фактическому пути посетителя — числитель
  // по пройденному, знаменатель по длине ЕГО пути (useQuizVisitorPath.ts).
  // Эти числа читают события аналитики; шапка — их же, но без экранов с
  // progressSkip (useQuizShownStep ниже).
  const [funnelGraph, , stepNumber, visitorPathTotal] =
    useQuizVisitorPath(
      screens,
      logic,
      isControlled,
      safeIndex,
      historyStack.history.length,
    );
  // Числа, которые ВИДИТ человек (шапка, блок «Шаг N из M»): без экранов с
  // progressSkip (quizShownStep.ts). Аналитика — по-прежнему числа выше.
  const [shownIndex, shownNumber, shownTotal] = useQuizShownStep(
    screens,
    historyStack.history,
    funnelGraph,
    safeIndex,
    isControlled,
    stepNumber,
    visitorPathTotal,
  );

  // Прогресс прохождения для событий воронки — «экран N из M». Документация
  // интегратора обещала эти поля у quiz_screen_view/quiz_answer с самого
  // начала, а рантайм их не слал: примеры кода в доке читали всегда-пустое
  // поле (находка ревизии).
  //
  // WEB-1812 — N и M СЧИТАЮТСЯ ПО ФАКТИЧЕСКОМУ ПУТИ ПОСЕТИТЕЛЯ (см. блок
  // «честный прогресс» выше): N — сколько экранов он реально прошёл, M —
  // длина его собственного пути (пройденное + остаток по безусловной
  // ступени). Раньше N была позицией в опубликованном списке, а M — длиной
  // этого списка, и прыжок правилом с экрана 3 на экран 30 из 40 давал долю
  // 0,75 у человека, ответившего на три вопроса: отметки quiz50/quiz90
  // ставились по построению. Дока интегратора описывает поля как «номер
  // текущего шага и всего шагов» — теперь это правда и для ветвящихся воронок.
  //
  // WEB-1153. Поля едут В ДВА МЕСТА: верхним уровнем payload и внутри
  // metadata. Верхний уровень — потому что QuizEventDto теперь их объявляет
  // (раньше не объявляла, и глобальный forbidNonWhitelisted отбил бы 400 всё
  // событие — шрам WEB-933 п.3), и только оттуда приём кладёт их в колонки
  // analytics_events.screen_index/screen_total. Дубль в metadata оставлен
  // намеренно: его уже читают интеграторы моста и старые события, а приём
  // понимает оба источника (верхний уровень главнее) — страницы у посетителей
  // закешированы, старые плееры ещё какое-то время шлют только metadata.
  //
  // ⚠ Значение здесь — С ЕДИНИЦЫ (контракт интегратора). У колонки
  // analytics_events.screen_index (WEB-254 B1a, миграция 1890000000040)
  // нумерация НУЛЕВАЯ, поэтому −1 делает приём
  // (backend/src/analytics/utils/screen-progress.util.ts), а доля метрик
  // считается как (screen_index + 1) / screen_total. Не «чинить» это здесь.
  const screenProgress = (): { screenIndex: number; screenTotal: number } => ({
    screenIndex: stepNumber,
    screenTotal: visitorPathTotal,
  });

  // Метка шага оплаты в адресе (WEB-744 этап 1.5, WEB-1740), SCREEN_VIEW с
  // пикселем и замер скорости экрана (WEB-1992) — useQuizScreenEvents.ts.
  // Вызов обязан стоять выше раннего выхода (правила хуков).
  useQuizScreenEvents(
    currentScreen,
    isPreview,
    isCompleted,
    funnel,
    projectId,
    quizSlug,
    stepNumber,
    screenProgress,
    track,
    perScreen,
  );

  // WEB-1796: узел авто-перехода живёт отдельным хуком (useAutoAdvance.ts) —
  // эффект попытки перехода (WEB-447 P1-3 + WEB-1540), счётчик завершений
  // ввода и пара «ключ + счётчик» (WEB-1643), признак застревания и таймер
  // ПОКАЗА запасной кнопки (WEB-1794). Вынесен без изменения поведения.
  //
  // Вызов ОБЯЗАН стоять выше раннего выхода по проверке способа оплаты
  // (правила хуков). Саму попытку перехода хук делает через ссылку, которую
  // визард заполняет ниже по рендеру (`handleNextRef.current = handleNext`):
  // handleNext объявлен уже ПОСЛЕ того раннего выхода.
  const {
    handleNextRef,
    rescueVisible: autoAdvanceRescueVisible,
    onScreenFocusOut: handleScreenFocusOut,
    onScreenKeyDown: handleScreenKeyDown,
  } = useAutoAdvance({
    screens,
    safeIndex,
    values,
    isCompleted,
    // Жалоба 18.09: память «на этом экране прыжок уже был» поднимается из
    // снимка и пополняется в нём же — иначе перезагрузка обнуляла её, а
    // ответы покупателя переживали, и «Назад» мгновенно уносило вперёд.
    restoredAutoAdvanced: restoredProgress?.autoAdvanced,
    onAutoAdvanced: rememberAutoAdvanced,
    // Решение владельца №4: пауза перед авто-переходом из пака (эталон 200 мс).
    advanceDelayMs: resolveAdvanceDelayMs(stylePackId),
  });

  // WEB-1993 п.3: пропсы блоков должны быть СТАБИЛЬНЫМИ — иначе `React.memo`
  // над `PublicBlockRenderer` не спасает ни один блок: новый объект позиции
  // шага и новая функция перехода на каждый рендер делают «разными» все блоки
  // экрана после любого тапа.
  //
  // Оба хука стоят ЗДЕСЬ, выше раннего выхода по проверке способа оплаты, по
  // той же причине, что и useAutoAdvance строкой выше: правила хуков. Ниже,
  // рядом с `handleNextRef.current = handleNext`, они ломали отрисовку экрана
  // с условием PAYMENT_METHOD («хуков больше, чем в прошлый раз»).
  // Сам переход зовётся через ссылку: `handleNext` объявлен уже после выхода.
  const handleNextStable = useCallback(
    () => handleNextRef.current?.() ?? false,
    [handleNextRef],
  );
  // Пачка настроек 2: настройки шапки/нижней кнопки и пауза перехода по
  // кнопкам (`states.advanceDelayMs`) — одним хуком. Пауза 0 — нажатие зовёт
  // переход сразу и синхронно, как было.
  const chromeKit = useQuizChromeKit();
  const chromeExtras = chromeKit.useQuizChromeExtras({
    stylePackId,
    quizHeader,
    screen: currentScreen,
    values,
    hiddenSet,
    isEditorPreview,
    quizCta,
  });
  const { runNav } = chromeExtras;
  const handleNextClick = useCallback(() => {
    runNav(handleNextStable);
  }, [runNav, handleNextStable]);
  // Задание stylepack/skip-link: ссылка «Пропустить» — тот же handleNext без ответа (skipAdvance.ts).
  const { onSkip: handleSkipClick, skippingRef } = useSkipAdvance(handleNextRef, runNav);
  // Один объект позиции на экран, а не по объекту на блок.
  const stepPosition = useMemo(
    () => ({ current: shownNumber, total: shownTotal }),
    [shownNumber, shownTotal],
  );

  // Починка ревизии («два быстрых тапа Далее»): снимает transitionInFlightRef
  // РОВНО тогда, когда currentIndex реально сменился — то есть переход,
  // запущенный через startTransition, уже закоммичен и виден на экране.
  // Должен стоять здесь, а не рядом с handleNext (тот объявлен ниже раннего
  // выхода по проверке способа оплаты) — по той же причине, что и остальные
  // хуки в этом блоке (правила хуков).
  useEffect(() => {
    transitionInFlightRef.current = false;
  }, [currentIndex]);

  /**
   * Дверь ухода для блоков экрана (`externalExitContext`).
   *
   * Значение памятуется ОДИН раз и зовёт последнюю реализацию через ref — тем
   * же приёмом, что `handleNextRef` ниже. Новый объект на каждый рендер
   * пересоздавал бы эффект блока «редирект» (значение двери стоит у него в
   * списке зависимостей) и перезапускал обратный отсчёт, то есть уход не
   * наступил бы никогда.
   *
   * Оба хука стоят ЗДЕСЬ, до раннего выхода по проверке способа оплаты: ниже
   * они вызывались условно (порядок хуков менялся, когда проверка
   * разрешалась) — правила хуков этого не допускают. Переприсваивание
   * `externalExitImplRef.current` осталось на прежнем месте: оно зовёт
   * `leaveQuizOutward`/`emitQuizComplete`, объявленные ниже.
   */
  const externalExitImplRef = useRef<ExternalExitContextValue | null>(null);
  const externalExitValue = useMemo<ExternalExitContextValue>(
    () => ({
      exitViaRedirectBlock: (blockId: string, target: string) =>
        externalExitImplRef.current?.exitViaRedirectBlock(blockId, target),
      markLinkExit: () => externalExitImplRef.current?.markLinkExit(),
      // WEB-2262: значения меток для адреса кнопки-ссылки — через тот же ref,
      // что и остальные двери: сам обработчик объявлен ниже по файлу.
      outboundMacroValues: () =>
        externalExitImplRef.current?.outboundMacroValues?.() ?? {},
      // WEB-3296: адрес точки ухода для кнопки-ссылки — тот же ref.
      linkExitHref: (blockId: string) =>
        externalExitImplRef.current?.linkExitHref?.(blockId) ?? null,
    }),
    [],
  );

  // F1d A8: if PAYMENT_METHOD condition is on screen 0 and probe has not yet resolved,
  // show loading spinner to prevent flash of wrong visibility state on first render.
  // Placed AFTER all hooks to respect Rules of Hooks (no early return before hooks).
  // On screens > 0, the probe has already resolved by the time the user arrives.
  const screen0 = screens[0];
  if (!paymentMethodReady && screen0 && needsPaymentOnScreen0(logic, screen0)) {
    return <Preloader data-testid="f1d-payment-probe-loading" />;
  }

  // WEB-1716: отправка лида и повтор недоехавшего (useQuizLeadDelivery.ts).
  const [sendLead, retryPendingLead] = createQuizLeadDelivery(
    projectId,
    pendingLeadRef,
    rememberPendingLead,
    setEmailDelivered,
  );
  // Второй конец находки 2 шестого прохода: эффект монтирования выше видит
  // повтор только через этот ref (сама функция объявлена после раннего
  // возврата рендера).
  retryPendingLeadRef.current = retryPendingLead;

  /**
   * Ревизия 45 (C6) — решение посетителя по блоку «Явное согласие на
   * чувствительные данные», действующее на ВСЁ прохождение, а не на свой
   * экран. Автоподстановка ставит один блок на воронку (перед первым
   * чувствительным вопросом), поэтому судить только по текущему экрану значило
   * бы собирать рост, вес и сон со следующих экранов как ни в чём не бывало.
   * Значения формы живут всё прохождение — решение читается по ним с любого
   * экрана. Не хук: считается на месте вызова, из зависимостей ничего не
   * держит.
   */
  const funnelSensitiveConsent = () => {
    // Проход по швам пачки 45, С1: судим ТОЛЬКО по экранам, которые посетитель
    // уже прошёл, плюс текущий. Источник — история переходов, а не порядок
    // `screens`: переходы правилами экраны пропускают, и порядок соврал бы про
    // «пройден». Блок согласия, до которого ещё не дошли, ответы верхних
    // экранов не запирает — галочку человек не видел.
    //
    // Проход по швам пачки 45, С2: множество скрытых блоков считается по КАЖДОМУ
    // пройденному экрану своим правилом показа, а не одним снимком текущего
    // экрана. Иначе блок, спрятанный автором правилом, исключался только на
    // своём экране, а со следующего снова запирал сбор — починка C11 работала
    // ровно один экран.
    const passedScreenIds = new Set<string>();
    const hiddenAcrossPassed = new Set<string>(hiddenSet);
    for (const index of [...historyStack.history, safeIndex]) {
      const passed = screens[index];
      if (!passed) continue;
      passedScreenIds.add(passed.id);
      if (index === safeIndex) continue;
      for (const id of resolveVisibility(passed.blocks ?? [], logic, evalCtx)) {
        hiddenAcrossPassed.add(id);
      }
    }
    return resolveFunnelSensitiveConsent(
      screens,
      currentScreen?.blocks ?? [],
      values,
      hiddenAcrossPassed,
      passedScreenIds,
    );
  };

  const submitEmailIfPresent = () => {
    if (emailCaptured || !currentScreen) return;
    // WEB-1456 (ревизия пачки 1, находка 1б): соседние вызовы этой же ветки
    // handleNext (findUnfilledRequired, collectScreenAnswers) уже фильтруют
    // по hiddenSet — email-поиск оставался единственным исключением, и почта
    // из поля, спрятанного правилом ПОСЛЕ заполнения (A4: hiding не стирает
    // ответ), всё равно уезжала в captureLeadPublic.
    const visibleBlocksForEmail = currentScreen.blocks.filter(
      (b: EditorBlock) => !hiddenSet.has(b.id),
    );
    const email = findEmailValue(visibleBlocksForEmail, values);
    if (!email) return;
    // Ревизия 45 (C7): почта — самый чувствительный ответ экрана, а уезжала
    // она МИМО замка: этот вызов стоит в handleNext выше `collectScreenAnswers`
    // и про согласие не знал вовсе. Текст блока при этом обещает посетителю,
    // что его ответы никуда не уходят. Тот же замок и то же правило, что у
    // сбора ответов (C6): решение действует на всё прохождение.
    const sensitiveConsent = funnelSensitiveConsent();
    if (sensitiveConsent.present && !sensitiveConsent.collectGranted) return;
    // WEB-848 (AC6): the editor's own preview (standalone ?preview=1 tab, or
    // the PhonePreview canvas — both set isPreview=true) must never write a
    // real lead row. Prior to this fix captureLeadPublic ran unconditionally
    // here even under isPreview — mirrors the guard PaywallScreenWizard's
    // startCheckout already had (`if (isPreview) return ...` before its own
    // lead-capture call).
    if (isPreview) {
      setEmailCaptured(true);
      setEmailDelivered(true);
      return;
    }
    const utm = getStoredUtm();
    const sessionId = getSessionId();
    // WEB-1480: решение посетителя о согласии (кука w2a_consent) едет и
    // сюда, а не только в track('EMAIL_SUBMIT') ниже. Пути два, гейт писем
    // на сервере стоит на обоих, но лид-путь до этой карточки знал только
    // страну обращения — вне ЕС она подразумевает согласие, и нажавший
    // «Отклонить» письмо всё равно получал. Арбитр — сервер
    // (resolveConsent); решения нет → поля нет (лишнее поле при
    // forbidNonWhitelisted отбило бы весь запрос, шрам clickIds WEB-933).
    const consent = resolvePlayerConsent();
    // Persist the lead — also triggers LEAD_REGISTRATION sequence server-side.
    sendLead({
      email,
      quizId,
      sessionId,
      ...(consent ? { consent } : {}),
      ...utm,
    });
    // Mirror as analytics event for funnel reporting.
    track('EMAIL_SUBMIT', {
      screenId: currentScreen.id,
      metadata: { email },
    });
    // Browser pixel: Lead event (fires per email-submit, mirroring the server
    // trackQuizEvent above; no-op if fbq not loaded / no consent). Only PageView
    // is globally one-shot — Lead/ViewContent fire on each trigger.
    void fireMetaBrowserEvent('EMAIL_SUBMIT');
    setEmailCaptured(true);
  };

  /**
   * fireQuizComplete — one-shot QUIZ_COMPLETE guard.
   * Multiple paths to completion (GOTO_ENDING, COMPLETE, cycle-guard fallback)
   * all go through this single function.
   */
  // WEB-189: emit the QUIZ_COMPLETE analytics event once (one-shot), WITHOUT
  // forcing the generic completion view — GOTO_ENDING renders the resolved
  // ending screen instead (see handleNext).
  const emitQuizComplete = () => {
    if (!oneShot.current.has('QUIZ_COMPLETE')) {
      oneShot.current.add('QUIZ_COMPLETE');
      track('QUIZ_COMPLETE');
    }
  };

  // Generic completion: emit + show the fallback "Thank you" view (used when no
  // custom ending screen is defined — COMPLETE / cycle-guard).
  // WEB-848: gated on !isControlled — in the editor's controlled preview the
  // completed/funnel-paywall terminal view must not hijack the whole canvas
  // away from whatever screen the editor has selected (same Вариант B
  // reasoning as the setCurrentIndex guards in handleNext below).
  const fireQuizComplete = () => {
    emitQuizComplete();
    if (!isControlled) setIsCompleted(true);
  };

  /**
   * WEB-2164 — АДРЕС СЕРВЕРНОЙ ТОЧКИ УХОДА для этой цели, либо `null`.
   *
   * Уход на чужую страницу оплаты совершаем МЫ, и это единственный переход
   * связки клиента, который можно померить без единого байта от покупателя:
   * браузерное событие на медленной сети и при быстром уходе теряется, а на
   * этом показателе планируется считать счёт (решение «Б1» зонта WEB-2231).
   *
   * `null` — «точку собрать не из чего», тогда вызывающий уходит ПРЯМО по уже
   * очищенному адресу. Два таких случая:
   *  · превью автора (`?preview=1`, канва конструктора) — квиз там не
   *    опубликован, точка ответила бы отказом, и автор увидел бы вместо своего
   *    перехода ошибку;
   *  · нет проекта/слага либо цель не опознана (решает `buildQuizExitUrl`).
   * Человек обязан дойти до оплаты даже там, где переход мы не посчитали
   * (п.6 требований карточки).
   */
  const resolveExitHref = (target: {
    ruleId?: string;
    blockId?: string;
  }): string | null => {
    if (isPreview) return null;
    const { screenIndex, screenTotal } = screenProgress();
    return buildQuizExitUrl({
      projectId,
      quizSlug,
      ruleId: target.ruleId,
      blockId: target.blockId,
      screenId: currentScreen?.id,
      sessionId: getSessionId(),
      // Ревизия 45 (C10) + WEB-3296: постоянный идентификатор посетителя
      // едет только при согласии, вместе с признаком «прочитан с устройства»
      // и самим решением (`consent`) — по ним сервер считает человека в
      // денежной записи (WEB-3295). Без согласия идентификатор жил бы в памяти
      // страницы и после перезагрузки был бы новым; ключом остаётся вкладка.
      ...resolveExitVisitorFields(),
      screenIndex,
      screenTotal,
      ...outboundMacroValues(),
    });
  };

  // WEB-2123 — значения меток для исходящего адреса, под гейтом согласия и
  // сигнала GPC (quizOutboundMacros.ts). Один вызов на обе ветки ухода.
  const outboundMacroValues = () =>
    quizOutboundMacroValues(screens, values, outboundAnswersBlocked);

  /**
   * ЕДИНЫЙ уход квиза наружу. Порядок строк критичен и один на обе уводящие
   * ветки (правило «увести по адресу» и блок «редирект»):
   *   1) событие завершения ложится в очередь,
   *   2) очередь досылается маяком,
   *   3) браузер уходит.
   * Наоборот — маяк уедет без события, и завершение потеряется вместе с
   * выгрузкой страницы (WEB-2124).
   *
   * `fallbackHref` — уже очищенный `safeHref` адрес: запасной путь, когда
   * точку собрать не из чего.
   */
  const leaveQuizOutward = (
    target: { ruleId?: string; blockId?: string },
    fallbackHref: string,
  ) => {
    emitQuizComplete();
    // WEB-1993 п.2: страница уходит ПО НАШЕЙ команде — ждать
    // `visibilitychange` не на что, хвост очереди досылаем прямо здесь.
    flushAnalyticsQueue({ transport: 'beacon' });
    // WEB-1911: уезжает только то, что прошло дверь очистки. Адрес серверной
    // точки ухода собирает наш же код (`buildQuizExitUrl`), запасной путь уже
    // очищен вызывающим — но правило «через дверь» одно на все адресные
    // ветки плеера, и показать это сторожу проще, чем держать исключение:
    // повторная очистка собранного нами адреса ничего в нём не меняет.
    const exitHref = safeHref(
      resolveExitHref(target) ?? fallbackHref,
      fallbackHref,
    );
    window.location.href = exitHref;
  };

  // Сами хуки двери ухода объявлены ВЫШЕ раннего выхода по проверке способа
  // оплаты (правила хуков) — здесь только переприсваивание реализации, оно
  // хуком не является.
  externalExitImplRef.current = {
    exitViaRedirectBlock: (blockId: string, target: string) => {
      leaveQuizOutward({ blockId }, target);
    },
    // Кнопка-ссылка: засчитываем завершение и досылаем очередь. Шаг «ушёл на
    // оплату» по ней не пишется (WEB-2164 п.6) — иначе шаг стал бы больше
    // числа завершений; через точку ухода она идёт ради денежной записи
    // (`linkExitHref` ниже, WEB-3296).
    markLinkExit: () => {
      emitQuizComplete();
      flushAnalyticsQueue({ transport: 'beacon' });
    },
    // WEB-2262 (решение владельца 18.09, вариант «а»): подставить метки в
    // адрес кнопки может только браузер — через серверную точку ухода кнопка
    // не идёт. Значения берём ТЕМ ЖЕ вызовом, что и серверная точка: гейт
    // согласия и сигнала GPC живёт внутри `outboundMacroValues`, второй копии
    // гейта у кнопки не заводится. Перевод «имена плеера → имена меток» —
    // одной функцией (`toOutboundMacroValues`), чтобы сопоставление не
    // разъехалось со строкой запроса точки ухода.
    outboundMacroValues: () => toOutboundMacroValues(outboundMacroValues()),
    // WEB-3296: опубликованная кнопка-ссылка уходит через серверную точку
    // ухода (цель — id блока; денежная запись перехода, WEB-3295). Аналитики
    // по ней сервер не пишет — шаг «ушёл на оплату» не растёт (WEB-2164 п.6).
    // Превью — `null`, кнопка уводит прямо.
    linkExitHref: (blockId: string) => resolveExitHref({ blockId }),
  };
  /**
   * WEB-1540: возвращает признак «продвижение состоялось».
   *
   * `false` — экран остался на месте по причине, которую человек может снять
   * сам: гейт обязательных полей (WEB-895) не пропустил. Ровно на этот ответ
   * смотрит авто-переход выше, чтобы не тратить свою единственную попытку.
   * `true` — цепочка решения отработала (переход, ветвление, завершение,
   * редирект, RESTART): экран сделал всё, что мог, повторять нечего.
   * Обработчики кликов возвращаемое значение игнорируют — их поведение
   * не изменилось.
   */
  const handleNext = (): boolean => {
    if (!currentScreen) return false;
    // Починка ревизии («два быстрых тапа Далее»): предыдущий тап уже запустил
    // переход экрана (startTransition), а он ещё не закоммичен — этот тап
    // (второй, случайный/быстрый) не выполняет ничего заново. Снимается
    // эффектом на смену currentIndex (см. useEffect над `stepPosition`).
    if (transitionInFlightRef.current) return false;
    const skip = skippingRef.current;

    // WEB-895: тумблер «Обязательное» реально гейтит продвижение — required
    // блоки текущего экрана без значения блокируют Next и получают warning.
    // WEB-1716: сюда же добавлена почта с опечаткой (`ivan@mail`) — по тому же
    // правилу, по которому создаётся лид. Раньше такой покупатель уходил
    // дальше без единой ошибки, а лид по нему не создавался вообще.
    const blockers = skip ? [] : findScreenBlockers(
      currentScreen.blocks,
      values,
      hiddenSet,
    );
    if (blockers.length > 0) {
      setRequiredWarnIds(new Set(blockers.map((b) => b.blockId)));
      return false;
    }
    if (requiredWarnIds.size > 0) setRequiredWarnIds(new Set());

    if (!skip) submitEmailIfPresent();
    // WEB-1716: лид, который не доехал с прошлого экрана, доезжает здесь.
    retryPendingLead();

    // Fire one SCREEN_ANSWER per answered block on the current screen.
    // WEB-2524: дубль того же значения подряд по паре «экран + блок» не
    // уходит, а смена значения (в том числе возврат к прежнему: A → B → A)
    // уходит снова — сервер считает последним ответом последний выбор.
    // F1c: pass hiddenSet so hidden blocks are excluded from SCREEN_ANSWER (A4).
    // Ревизия 45 (C6): решение по блоку согласия приносит плеер — он один
    // видит все экраны воронки (см. funnelSensitiveConsent).
    const answers = skip ? [] : collectScreenAnswers(
      currentScreen.blocks,
      values,
      hiddenSet,
      funnelSensitiveConsent(),
    );
    for (const ans of answers) {
      const slot = `${currentScreen.id}:${ans.blockId}`;
      if (lastSentAnswer.current.get(slot) === ans.value) continue;
      lastSentAnswer.current.set(slot, ans.value);
      track('SCREEN_ANSWER', {
        // WEB-1153: наверх — для колонок аналитики, в metadata — для моста.
        ...screenProgress(),
        screenId: currentScreen.id,
        metadata: {
          blockId: ans.blockId,
          blockType: ans.blockType,
          value: ans.value,
          // WEB-1140: текст вопроса, который видел пользователь (web2wave-
          // модель «вопрос → ответ»). Пусто → поле не шлём, бэкенд падает
          // обратно на blockId. В дедуп-ключ label намеренно не входит.
          ...(ans.label !== undefined && { label: ans.label }),
          ...screenProgress(),
        },
      });
    }

    // F1b: cycle-guard — checked BEFORE the transition.
    // transitions counter persists through RESTART (anti-pattern #6 prevention).
    if (historyStack.isGuardTripped()) {
      if (!oneShot.current.has('cycle_guard_tripped')) {
        oneShot.current.add('cycle_guard_tripped');
        // WEB-1435: экран, на котором сработал сторож, — для панели
        // «Аудит → Трафик → Зацикливания» (группировка по screen_id).
        track('cycle_guard_tripped', { screenId: currentScreen.id });
      }
      fireQuizComplete();
      // WEB-1540: сработавший сторож зацикливания — это ЗАВЕРШЕНИЕ квиза, а не
      // «попробуй ещё раз»: авто-переходу здесь повторять нечего.
      return true;
    }

    // F1b+F1d: build EvalContext from current player state with real sources.
    // WEB-1466: answers — ТОЛЬКО из varCtx.answers (единая точка приведения
    // форм ответа, ею же живут интерполяция и видимость, см. :317-323 и :360).
    // Прежняя самодельная сборка из сырых values отдавала multiple_choice
    // склеенной строкой 'o1,o2' — is_one_of сравнивал всю строку целиком
    // (operators.ts:130-137), ветвление по мультивыбору не срабатывало
    // никогда, а contains работал как поиск подстроки. A4-контракт тот же:
    // varCtx.answers включает ответы скрытых блоков (перебирает все непустые
    // values).
    if (skip) clearScreenValues(currentScreen.blocks, values, setValue);
    const evalContext: EvalContext = {
      answers: skip ? answersWithoutScreen(varCtx.answers, currentScreen.blocks) : varCtx.answers,
      variables,
      urlParams: varCtx.urlParams,
      geoCountry: geoCountry ?? null,
      deviceOs: syncSources.deviceOs,         // F1d: real UA-detected OS
      browser: syncSources.browser,            // F1d: real UA-detected browser
      browserInApp: syncSources.browserInApp,  // F1d: real in-app detection
      paymentMethod,                            // F1d: async Stripe probe result
      answerUnits,
    };

    // F1b: capture clicked single_choice option for tier 2 (D6: not multiple_choice).
    // WEB-1456 (ревизия пачки 1, находка 1а): скрытый правилом блок (hiddenSet)
    // не должен продолжать диктовать переход по своему nextScreenId — экран
    // не показал посетителю ни блок, ни выбранный вариант. Зеркало фикса
    // WEB-1107 для visibleBlocks (кнопка-запаска ниже, :796-798).
    const singleChoiceBlock = currentScreen.blocks.find(
      (b: EditorBlock) =>
        b.type === 'single_choice' && !hiddenSet.has(b.id) && values[b.id],
    );
    // Выбор картинкой: ветвится по варианту одиночный — с автопереходом и с
    // кнопкой «Далее» (решение владельца 22.10.2026, D6 расширено).
    // Шов skip-link × image-choice-auto (сведение stylepack/integration):
    // «Пропустить» — переход как при пустом ответе и для картинки тоже, ответ
    // экрана снят выше (clearScreenValues), а `values` здесь — ещё прежние.
    const clickedOption = skip
      ? undefined
      : singleChoiceBlock
        ? {
            blockType: 'single_choice' as const,
            nextScreenId: singleChoiceBlock.options?.find(
              (o) => o.id === values[singleChoiceBlock.id],
            )?.nextScreenId,
          }
        : singleImageChoicePick(currentScreen.blocks, hiddenSet, values);

    // F1b: run the decision chain.
    // WEB-848: currentIndex: safeIndex — in controlled mode the decision
    // chain must reason about the screen actually on display, not wherever
    // the (frozen) internal currentIndex state last was.
    const transition = decideTransition({
      screens,
      logic,
      currentScreen,
      currentIndex: safeIndex,
      clickedOption,
      context: evalContext,
      // WEB-3819: «перейти к пейволлу воронки» исполняет только плеер квиза.
      allowPaywallJump: true,
    });

    // F1b: dispatch the action — ветки исхода (переход, экран результата,
    // RESTART, уход по адресу, завершение, переход к пейволлу WEB-3819) —
    // quizTransitionDispatch.ts.
    dispatchQuizTransition(transition, {
      screens,
      safeIndex,
      isControlled,
      historyStack,
      transitionInFlightRef,
      setCurrentIndex,
      fireQuizComplete,
      emitQuizComplete,
      leaveQuizOutward,
      resetValues,
      setVariables,
      setEmailCaptured,
      setEmailDelivered,
      rememberPendingLead,
      setAutoAdvancedBlocks,
    });

    // QUIZ_START one-shot — fires on first forward navigation from screen 0.
    if (safeIndex === 0 && !oneShot.current.has('QUIZ_START')) {
      oneShot.current.add('QUIZ_START');
      track('QUIZ_START');
    }
    return true;
  };

  // F1b: Back = pop history (not index-1 which was wrong after GOTO jumps).
  // WEB-848: controlled mode keeps the preview on the editor-selected screen
  // for Back too — same "still runs, doesn't move it" reasoning as handleNext.
  const handleBack = () => {
    const prev = historyStack.pop();
    if (prev !== undefined && !isControlled) {
      setCurrentIndex(prev);
    }
  };
  // Пачка настроек 2: «назад» с той же паузой пака, что и «Далее».
  const handleBackClick = (): void => {
    runNav(handleBack);
  };

  // WEB-447 P1-3: apply the queued auto-advance (see the effect above the
  // payment-probe early-return). handleNextRef is assigned each render so the
  // effect always calls the latest closure.
  handleNextRef.current = handleNext;


  if (isCompleted) {
    // WEB-744 §3: шаг после квиза — встроенный пейволл воронки либо заглушка
    // «Спасибо» (QuizCompletedStep.tsx).
    return renderQuizCompletedStep(
      funnel,
      projectId,
      servedStale,
      language,
      stepNumber,
      stylePackId,
    );
  }

  if (!currentScreen) return null;

  // WEB-1107 (доводка, аудит 22.08): владелец воронки может навесить правило
  // видимости на блок «Кнопка Далее» (RuleEditor.tsx:84-89 предлагает такую
  // цель). Когда правило прячет блок, его самого на экране нет (рендер ниже
  // выбрасывает скрытые — hiddenSet.has(block.id)), а признаки «есть чем
  // продвинуться» должны считаться по ТОМУ ЖЕ множеству, иначе запасная
  // кнопка гаснет вместе со скрытым блоком и экран становится тупиком.
  // Зеркало пейволльного visibleBlocks (PaywallScreenWizard.tsx:558-559).
  const visibleBlocks = currentScreen.blocks.filter(
    (b: EditorBlock) => !hiddenSet.has(b.id),
  );

  // WEB-1709: кто на этом экране идёт сразу ЗА блоком с явно заданным
  // расстоянием снизу. Такому соседу общий рендер ставит обёртку расстояния и
  // без его собственной настройки — иначе неявное верхнее поле его корня (у
  // `<p>`/`<h1>` оно приходит от таблицы браузера) схлопывалось бы с заданным
  // и держало пол в 16px: автор ставил 0, а видел 16. Разбор и замеры — в
  // blockSpacing.tsx. Считаем по ВИДИМЫМ блокам: скрытого правилом соседа на
  // экране нет, и «предыдущим» он быть не может.
  const spacingFollowerIds = blocksFollowingExplicitSpacing(visibleBlocks);

  // WEB-927: эффективная раскладка экрана — тем же резолвером, что считают
  // пейволл и превью конструктора (иначе «в превью одно, в проде другое»).
  const screenLayout = resolveScreenLayout(currentScreen, stylePackId);

  // WEB-927 атом 3: оси раскладки двигают ТОЛЬКО поток блоков. Хром (прогресс,
  // лого, «назад», подпись раздела) остаётся первым узлом экрана, то есть
  // вверху, при любой оси — центрировать экран целиком нельзя, иначе прогресс
  // уедет в середину. Легаси-оси (top + center) обёртки не заводят вовсе:
  // разметка выкаченных воронок остаётся байт-в-байт прежней.
  // WEB-1713 — КТО ИЗ ДВУХ ИНДИКАТОРОВ УСТУПАЕТ.
  //
  // Инвариант WEB-785 остаётся прежним: на экране ровно ОДИН индикатор
  // прогресса, задвоения быть не должно. Менялось то, кто уступает. Раньше
  // молча выбрасывался БЛОК, который автор поставил руками, — и «Текст
  // прогресса» не показывался вовсе ни в превью, ни на публичной странице
  // (репорт владельца: «его не видно и непонятно, как его посмотреть»).
  // Увидеть его можно было только выключив шапку целиком, а вместе с ней
  // пропадала кнопка «назад» — то есть настройки у блока не было никакой.
  //
  // Теперь уступает АВТОМАТИЧЕСКИЙ индикатор шапки, и только на том экране,
  // где стоит блок. Гасится ровно индикатор: лого, подпись раздела и кнопка
  // «назад» в шапке остаются. Скрытый правилом видимости блок в счёт не идёт —
  // иначе экран остался бы вообще без индикатора.
  //
  // `progress_bar` сюда НЕ входит намеренно: он стоит почти на каждом экране
  // шаблонных воронок, и его расчехление сдвинуло бы вид живых воронок без
  // запроса владельца. Для него гард ниже остаётся прежним.
  const screenHasOwnProgressBlock = (currentScreen?.blocks ?? []).some(
    (b: EditorBlock) => b.type === 'progress_text' && !hiddenSet.has(b.id),
  );

  const chromeNode = (() => {
    // Хром экрана из стиль-пака (референсы 12min / BetterMe / AddMile /
    // Astroline). Без пака — прежний текстовый счётчик и текстовая
    // кнопка «назад»; settings.showProgress === false скрывает хром
    // целиком и остаётся сильнее пака.
    const pack = stylePackId ? getStylePackById(stylePackId) : undefined;
    // «Отступ под шапкой» из пака (WEB-3718); своя шапка воронки — прежние 16.
    const headerGap = quizHeader ? 16 : (pack?.chrome.headerGapPx ?? 16);
    const chromeHidden = showProgress === false;
    // WEB-916 п.1: в превью редактора экран выбирается извне (controlled), стек
    // истории не растёт вовсе — счётчик навсегда застревал на «Шаг 1 из M»,
    // хотя блок «Шаг X из Y» на том же экране показывал верное число (ему
    // передаётся safeIndex). В controlled-режиме считаем по показанному экрану;
    // на публике счётчик по-прежнему идёт по фактически пройденным шагам —
    // после GOTO-перехода индекс экрана и номер шага расходятся законно.
    // WEB-1812: сам счёт (stepIndex/stepNumber) и знаменатель пути живут выше
    // по файлу — их же читают события аналитики, чтобы экран и статистика
    // никогда не расходились.
    // Слово владельца 20.10.2026: поля шапки воронки перебивают пак — одна
    // дверь resolveQuizHeader (quizHeader.ts); нет поля — как решает пак.
    const header = resolveQuizHeader(pack?.chrome, quizHeader);
    const progressKind = header.progressKind;
    // WEB-1713: на экране с блоком «Текст прогресса» автоматический индикатор
    // шапки не рисуется — роль индикатора берёт блок автора; так же — у экрана
    // с полем hideProgress (QuizHeaderProgress.tsx). Гасится ровно индикатор
    // (полоса/сегменты/узлы/счётчик), остальная шапка на месте.
    const showAutoProgress = quizHeaderShowsProgress(screenHasOwnProgressBlock, currentScreen);
    // Поле экрана hideHeaderBack гасит только «назад» шапки (quizHeaderScreen.ts).
    const backKind = headerBackKind(header.backKind, currentScreen);
    const fillColor = header.fillColor;
    const trackBg = header.trackColor ?? 'var(--color-border-primary, #f0f0f0)';
    const closeNode = header.close ? (
      <chromeKit.QuizCloseButton
        kind={header.close.kind}
        // Адрес автора — через общую дверь ссылок плеера (сторож
        // publicRendererHref.web1911), а не на веру разбору шапки.
        href={safeHref(header.close.href)}
        language={language}
        blockNavigation={isEditorPreview}
        iconColor={header.iconColor}
      />
    ) : null;
    // WEB-1812: доля полосы — по длине пути ПОСЕТИТЕЛЯ, не по длине списка.
    const ratio = shownTotal > 0 ? shownNumber / shownTotal : 0;

    // WEB-922: chrome.logo — лого воронки. 'none' скрывает; без пака
    // заданный URL рендерится по центру (дефолт).
    // WEB-929: лого живёт отдельно от прогресса — тумблер
    // settings.showProgress его больше не гасит.
    // Пачка настроек 2: 'desktop-left' рисует DesktopLogo вне шапки, поле
    // экрана hideLogo гасит лого на этом экране.
    // Слово владельца 21.10.2026: положение и высота — поля шапки
    // (logoAlign, logoHeight) перебивают пак, дверь resolveQuizHeader.
    const logo =
      logoUrl && header.logo && !chromeExtras.visibility.hideLogo ? header.logo : null;
    const logoH = logo?.height ?? QUIZ_HEADER_LOGO_DEFAULT_HEIGHT;
    // По центру своим полем — В СТРОКЕ кнопок, поверх середины (вне потока):
    // «назад» и крестик у краёв, строка не ниже логотипа. Середина занята
    // полосой (one-row) или текстом «Шаг N из M» — своей строкой над кнопками.
    const middleBusy =
      header.oneRow ||
      (progressKind === 'legacy' && showAutoProgress && header.legacyCounter);
    // Слово владельца 21.10.2026: раскладка 'logo-row' — логотип в строке
    // «назад — логотип — счётчик» (QuizHeaderRow.tsx), не над ней и не поверх.
    const logoInRow = header.logoRow && progressKind !== 'section-nodes';
    const logoInline = !logoInRow && logo?.own === true && logo.align === 'center';
    const logoImg = logo ? (
      <img
        src={logoUrl}
        alt=""
        data-testid="quiz-logo"
        // WEB-1994: высота фиксирована (24 или поле) — по вертикали вёрстку не
        // двигает; ширина по пропорции; разбор картинки — не на главном потоке.
        decoding="async"
        style={{
          height: logoH,
          maxWidth: logo.height || logoInline ? '100%' : 160,
          objectFit: 'contain',
          display: 'block',
        }}
      />
    ) : null;
    const logoNodeIn = (busy: boolean) =>
      logo && !logoInRow && !(logoInline && !busy) ? (
        <div data-logo-place="row" style={logoOnly.logoOwnRowStyle(logo.align, header.rowGap)}>
          {logoImg}
        </div>
      ) : null;
    const inlineLogoIn = (busy: boolean) =>
      logoImg && logoInline && !busy ? (
        <div data-logo-place="inline-center" style={logoOnly.LOGO_INLINE_CENTER_STYLE}>
          {logoImg}
        </div>
      ) : null;
    const inlineRowStyle = (busy: boolean): React.CSSProperties =>
      logoImg && logoInline && !busy ? { position: 'relative', minHeight: logoH } : {};
    // Поле шапки counterTotal — «всего» только в тексте счётчика (quizHeaderScreen.ts).
    const counterTotal = headerCounterTotal(header.counterTotal, shownTotal, shownNumber);
    const counterNode = (
      <QuizStepCounter
        current={shownNumber}
        total={counterTotal}
        style={header.counterStyle}
        fontSize={header.counterFontSize}
        fontWeight={header.counterFontWeight}
        accentColor={fillColor}
        look={header.counterLook}
        language={language}
      />
    );

    // Пачка настроек 2: шапка скрыта полем экрана (или клавиатурой) — без
    // распорки, контент поднимается. Слово владельца 21.10.2026: поле экрана
    // оставляет логотип на своём месте (клавиатура — нет), QuizLogoOnlyChrome.tsx.
    if (chromeExtras.visibility.hideChrome) {
      const sectionNodes = progressKind === 'section-nodes' && !chromeHidden;
      const { visibility } = chromeExtras;
      const desktop = desktopViewport;
      if (!logo || !logoOnly.keepLogoWhenChromeHidden({ visibility, logo, sectionNodes, desktop })) {
        return null;
      }
      return (
        <logoOnly.QuizLogoOnlyChrome
          logo={logoImg}
          align={logo.align}
          height={logoH}
          place={logoOnly.logoOnlyPlace({ logoInRow, logoInline, sectionNodes })}
          spacer={renderQuizBackSlot(
            backKind,
            true,
            handleBackClick,
            language,
            chromeKit,
            header.iconColor,
          )}
          layoutStyle={{ ...screenChromeLayoutStyle(screenLayout), ...quizHeaderInsetStyle(header) }}
        />
      );
    }
    const forwardNode = chromeExtras.forwardEnabled ? (
      <chromeKit.ForwardChevronButton
        visible={chromeExtras.forwardVisible}
        onClick={handleNextClick}
        label={getPublicQuizStrings(language).continueLabel}
        onPressKeepFocus={keepFieldFocusOnPress}
      />
    ) : null;
    // Пачка настроек 2: «узлы по разделам» — своя строка эталона Slimkit.
    if (progressKind === 'section-nodes' && !chromeHidden) {
      const sectionHeader = (
        <chromeKit.QuizSectionHeader
          onBack={!isFirst && backKind !== 'none' ? handleBackClick : null}
          backLabel={getPublicQuizStrings(language).backLabel}
          forward={
            chromeExtras.forwardEnabled
              ? {
                  visible: chromeExtras.forwardVisible,
                  onClick: handleNextClick,
                  label: getPublicQuizStrings(language).continueLabel,
                }
              : null
          }
          progress={
            showAutoProgress
              ? chromeKit.sectionProgressForVisitor({
                  screens,
                  history: historyStack.history,
                  graph: funnelGraph,
                  currentIndex: safeIndex,
                  isControlled,
                })
              : null
          }
          onPressKeepFocus={keepFieldFocusOnPress}
        />
      );
      const sectionRow = closeNode ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>{sectionHeader}</div>
          {closeNode}
        </div>
      ) : (
        sectionHeader
      );
      // Логотип у «узлов по разделам» — только своим полем положения (у пака
      // эта шапка без логотипа), своей строкой: строку узлов не трогаем.
      return logo?.own ? (
        <>
          {logoNodeIn(true)}
          {sectionRow}
        </>
      ) : (
        sectionRow
      );
    }

    // Кнопка «Назад» или её невидимая распорка — один слот на обе ветки шапки
    // (QuizBackSlot.tsx, ревизия пачки 38, WEB-2092).
    const backSlot = renderQuizBackSlot(
      backKind,
      isFirst,
      handleBackClick,
      language,
      chromeKit,
      header.iconColor,
    );
    const logoRowOf = (counter: React.ReactNode, trailing: React.ReactNode) => (
      <QuizLogoRow
        back={backSlot}
        logo={logoImg}
        logoAlign={logo?.align ?? 'center'}
        counter={counter}
        trailing={trailing}
      />
    );

    if (chromeHidden && logoInRow) {
      return (
        <div
          data-testid="quiz-screen-chrome"
          style={{ marginBottom: headerGap, ...screenChromeLayoutStyle(screenLayout), ...quizHeaderInsetStyle(header) }}
        >
          {logoRowOf(null, closeNode)}
        </div>
      );
    }
    if (chromeHidden) {
      return (
        <div
          data-testid="quiz-screen-chrome"
          style={{ marginBottom: headerGap, ...screenChromeLayoutStyle(screenLayout), ...quizHeaderInsetStyle(header) }}
        >
          {logoNodeIn(false)}
          {/* WEB-2092 п.2: «Назад» слева — как в ветке обычной шапки ниже.
              Оба конца правила правятся вместе (сторож
              __tests__/QuizScreenWizard.backAlign.web2092.test.tsx). */}
          <div
            style={{
              display: 'flex',
              justifyContent: closeNode ? 'space-between' : 'flex-start',
              alignItems: 'center',
              ...inlineRowStyle(false),
            }}
          >
            {backSlot}
            {closeNode}
            {inlineLogoIn(false)}
          </div>
        </div>
      );
    }

    // Дополнение слова владельца 20.10.2026 (сверка с Mojo): раскладка шапки
    // «в одну строку» ставит полосу между «назад» и крестиком, растянутой;
    // нет поля — полоса отдельной строкой под кнопками, как раньше.
    const oneRow = header.oneRow;
    // Слово владельца 21.10.2026: размер узла своим полем (progressNodeSize).
    const nodeBox = progressNodeBox(header.nodeSize, header.thickness ?? 2);
    const progressBars = (
      <>
        {progressKind === 'section-steps' && showAutoProgress && (
          <chromeKit.QuizSectionStepsProgress header={header} flush={oneRow} input={{ screens, history: historyStack.history, graph: funnelGraph, currentIndex: safeIndex, isControlled }} />
        )}
        <QuizHeaderProgressBars
          kind={progressKind}
          show={showAutoProgress}
          oneRow={oneRow}
          total={shownTotal}
          stepIndex={shownIndex}
          ratio={ratio}
          thickness={header.thickness}
          fillColor={fillColor}
          fillBackground={header.fillBackground}
          trackBg={trackBg}
          nodeBox={nodeBox}
        />
      </>
    );

    return (
      <div
        data-testid="quiz-screen-chrome"
        style={{ marginBottom: headerGap, ...screenChromeLayoutStyle(screenLayout), ...quizHeaderInsetStyle(header) }}
      >
        {logoNodeIn(middleBusy)}
        {logoInRow &&
          logoRowOf(
            showAutoProgress &&
              (progressKind === 'legacy' ? header.legacyCounter : header.counter)
              ? counterNode
              : null,
            <>
              {forwardNode}
              {closeNode}
            </>,
          )}
        {!logoInRow && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--color-text-secondary, #8c8c8c)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              ...inlineRowStyle(middleBusy),
            }}
          >
            {/* WEB-2092 п.2: кнопка «Назад» — ПЕРВАЯ в строке, счётчик уезжает во
                второй слот. Раньше первым шёл счётчик (а без него — пустая
                распорка), и space-between прижимал кнопку к правому краю.
                Слот кнопки сохраняется распоркой на первом экране — иначе
                счётчик стоял бы слева на первом шаге и справа на всех остальных,
                то есть шапка дёргалась бы при каждом переходе. У пака с
                backButton: 'none' кнопки нет ни на одном шаге — там распорки нет
                вовсе (Н3), прыгать нечему. Вторая ветка — шапка со скрытым хромом
                выше. */}
            {backSlot}
            {progressKind === 'legacy' && showAutoProgress && header.legacyCounter ? (
              <span data-testid="quiz-progress">
                {/* WEB-785: счётчик локализуется по языку воронки. */}
                {getPublicQuizStrings(language).stepOf(shownNumber, counterTotal)}
              </span>
            ) : oneRow ? (
              <div style={{ flex: 1, minWidth: 0 }}>{progressBars}</div>
            ) : (
              <span />
            )}
            {forwardNode}
            {closeNode}
            {inlineLogoIn(middleBusy)}
          </div>
        )}

        {oneRow ? null : progressBars}

        {(pack?.chrome.progressLabel !== 'none' && pack) ||
        (header.counter && progressKind !== 'legacy' && !logoInRow) ? (
          <div
            style={{
              display: 'flex',
              justifyContent:
                pack?.chrome.progressLabel === 'left' ? 'space-between' : 'center',
              alignItems: 'center',
              marginTop: 6,
              gap: 8,
            }}
          >
            {pack?.chrome.progressLabel !== 'none' &&
            isMeaningfulScreenName(currentScreen.name) ? (
              <span
                data-testid="quiz-section-label"
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  // WEB-922: регистр оверлайна из стиль-пака; фолбэк
                  // uppercase сохраняет прежний вид без пака.
                  textTransform: 'var(--overline-transform, uppercase)' as React.CSSProperties['textTransform'],
                  color: 'var(--color-primary, #1677ff)',
                }}
              >
                {currentScreen.name}
              </span>
            ) : (
              <span />
            )}
            {header.counter &&
              progressKind !== 'legacy' &&
              showAutoProgress &&
              !logoInRow &&
              counterNode}
          </div>
        ) : null}
      </div>
    );
  })();

  // WEB-922 (возврат QA 2026-08-01): признак «на экране есть незаполненные
  // required» считается ОДИН раз на рендер и питает обе кнопки продвижения —
  // блок «Кнопка Далее» и системную запасную (ctaNode ниже). Раньше он жил
  // внутри ветки button_next, поэтому на сгенерированных экранах без блока-
  // кнопки приглушение не появлялось никогда.
  const screenHasUnfilledRequired =
    findScreenBlockers(currentScreen.blocks, values, hiddenSet).length > 0;

  // Пачка настроек 2: вид кнопки «Далее» из пака (пусто у прежних паков —
  // тогда проп не передаётся вовсе и memo блока не сбивается).
  // Слово владельца 21.10.2026: вид выключенной — поле settings.quizCta
  // сильнее пака (переменные приглушения на самой кнопке, quizCta.ts).
  const packNextStyleRaw = {
    ...chromeExtras.cta.vars,
    ...chromeKit.ctaPackButtonStyle(chromeExtras.pack, screenHasUnfilledRequired, chromeExtras.cta.disabledStyle),
  };
  const packNextButtonStyle =
    Object.keys(packNextStyleRaw).length > 0 ? packNextStyleRaw : undefined;

  const blockNodes = currentScreen.blocks.map((block: EditorBlock) => {
    // F1c: skip hidden blocks entirely — do not render, do not collect answers.
    // WEB-2473 п.2: на холсте (isControlled) — полупрозрачно с плашкой.
    const ruleHidden = hiddenSet.has(block.id);
    if (ruleHidden && !isControlled) return null;
    // WEB-785 (ретест QA 27.07 «двойной счётчик» + возврат QA 01.08 «разные
    // индикаторы на разных экранах»): шапка квиза уже рисует индикатор
    // прогресса (quiz-progress выше) по ФАКТИЧЕСКОЙ позиции. Любой
    // progress-блок на экране — второй индикатор: у progress_text свои
    // progressCurrent/Total из генерации (расходящиеся числа), а
    // progress_bar генерация кладёт лишь на ЧАСТЬ экранов — отсюда «на
    // первом экране индикатор один, на остальных другой». Первый заход
    // закрыл только progress_text, поэтому кейс QA пережил гард.
    // Код-гард вместо надежды на промпт (урок WEB-781): при видимой шапке
    // блок-дубль не рендерится ни в одном из двух видов; при скрытой
    // (showProgress=false) блок остаётся единственным индикатором.
    // WEB-1713: `progress_text` из этого гарда ВЫВЕДЕН. Инвариант «один
    // индикатор на экране» он не нарушает — на экране с этим блоком шапка
    // сама не рисует свой индикатор (showAutoProgress выше). Уступает
    // автоматический, а не поставленный автором: раньше блок не показывался
    // нигде и настроить его было нечем (репорт владельца 24.08).
    // `progress_bar` остаётся под гардом: он стоит почти на каждом экране
    // шаблонных воронок, и его расчехление сдвинуло бы вид живых воронок.
    if (block.type === 'progress_bar' && showProgress !== false) {
      return null;
    }
    // WEB-848: в превью редактора (isPreview, mirrors WEB-846 for the
    // paywall wizard) каждый блок оборачивается в [data-block-id] —
    // делегированный клик в EditorPreviewZone ищет именно этот атрибут,
    // без него клик по блоку на квизе не открывал бы свойства (это и
    // есть AC7). На публичной/preview-странице разметка сохраняет тот
    // же вид, что и у пейволла (лишний div там безвреден).
    const selectable = (shown: React.ReactElement): React.ReactElement => {
      const rendered = ruleHidden ? <HiddenByRuleMark key={block.id} blockId={block.id}>{shown}</HiddenByRuleMark> : shown;
      return isPreview ? (
        <div
          key={block.id}
          data-block-id={block.id}
          className="editor-preview-block"
        >
          {rendered}
        </div>
      ) : (
        rendered
      );
    };
    if (block.type === 'button_next') {
      const nextDock = dockUnlessSkipOnly(block, chromeExtras.dock);
      // WEB-1696: ОДНА ДВЕРЬ. Раньше визард рисовал кнопку «Далее» своей
      // разметкой — и мимо неё проходили все авторские настройки блока сразу:
      // свой кегль, «Фон кнопки», «Цвет текста внутри элемента», «Фон блока»,
      // внутренние отступы, расстояние до соседей, шрифт блока. Все они
      // применяются в общей точке PublicBlockRenderer, до которой этот тип
      // просто не доходил (замер: разметка того же блока через общий рендер
      // несёт их все, разметка экрана — ни одной).
      //
      // Теперь кнопку рисует общий рендер, а визард передаёт ему ровно то,
      // чего у рендера быть не может: обработчик перехода, свой data-testid и
      // WEB-922-приглушение при незаполненных обязательных блоках.
      const nextNode = selectable(
        <PublicBlockRenderer
          key={block.id}
          block={block}
          textColor={textColor}
          varCtx={varCtx}
          buttonStyle={buttonStyle}
          stylePackId={stylePackId}
          stepPosition={stepPosition}
          onAdvance={handleNextClick}
          onSkip={handleSkipClick}
          onBack={isFirst ? undefined : handleBackClick}
          advanceTestId="quiz-next-button"
          advanceDimmed={screenHasUnfilledRequired}
          followsExplicitSpacing={spacingFollowerIds.has(block.id)}
          advanceFallbackLabel={
            isLast
              ? getPublicQuizStrings(language).finishLabel
              : getPublicQuizStrings(language).continueLabel
          }
          advanceButtonStyle={packNextButtonStyle}
          advanceWrapStyle={nextDock ? chromeKit.DOCKED_WRAP_STYLE : undefined}
        />,
      );
      // Пачка настроек 2: пак 'fixed-bottom' — блок прибит к низу окна.
      return nextDock ? (
        <chromeKit.CtaDock
          key={block.id}
          config={nextDock}
          canScroll={chromeExtras.canScroll}
          preview={isEditorPreview}
        >
          {nextNode}
        </chromeKit.CtaDock>
      ) : (
        nextNode
      );
    }
    // WEB-906: hero-CTA была декоративной кнопкой без обработчика —
    // «Начать» не работала. Клик по hero с ctaText продвигает экран.
    // WEB-919: продвигает ВСЕГДА, а не только вне превью. PM смотрит
    // воронку по ссылке `?preview=1` — это публичное превью черновика, где
    // навигация работает (страховочная кнопка там кликалась), и ровно
    // поэтому «Начать не работает» у него было правдой. Блок button_next
    // здесь же вызывает handleNext без оглядки на isPreview — hero-CTA
    // теперь ведёт себя так же, тем более что страховку мы убрали.
    if (
      block.type === 'hero' &&
      typeof (block as { ctaText?: unknown }).ctaText === 'string' &&
      ((block as { ctaText: string }).ctaText.trim().length > 0)
    ) {
      return selectable(
        <div
          key={block.id}
          data-testid={`advance-fallback-${block.id}`}
          onClick={handleNextClick}
          style={{ cursor: 'pointer' }}
        >
          <PublicBlockRenderer
            block={block}
            textColor={textColor}
            varCtx={varCtx}
            buttonStyle={buttonStyle}
            stylePackId={stylePackId}
            stepPosition={stepPosition}
            followsExplicitSpacing={spacingFollowerIds.has(block.id)}
          />
        </div>,
      );
    }
    // WEB-895: warning под обязательным блоком после попытки пройти
    // дальше; исчезает, как только значение появилось.
    // WEB-1716: причина считается тут же — подпись у опечатки в почте своя
    // («проверьте адрес»), иначе покупателю показывали «Это поле
    // обязательное» на заполненном поле и он не понимал, чего от него хотят.
    // WEB-2351: у числа вне границ — своя подпись с границами автора.
    const blockWarn = requiredWarnIds.has(block.id)
      ? findScreenBlockers([block], values, hiddenSet)[0]
      : undefined;
    const blockWarnReason: ScreenBlockerReason | undefined = blockWarn?.reason;
    return selectable(
      <Fragment key={block.id}>
        <PublicBlockRenderer
          block={block}
          textColor={textColor}
          varCtx={varCtx}
          buttonStyle={buttonStyle}
          stylePackId={stylePackId}
          // WEB-832 п.4: фактическая позиция — после прыжка по логике
          // авторские цифры блока «Шаг X из Y» перестают соответствовать.
          stepPosition={stepPosition}
          followsExplicitSpacing={spacingFollowerIds.has(block.id)}
          {...microFeedbackRevealProps(block, visibleBlocks)}
        />
        {blockWarnReason && (
          <div
            role="alert"
            data-testid={`required-warning-${block.id}`}
            data-warning-reason={blockWarnReason}
            style={{
              color: 'var(--color-error, #ff4d4f)',
              fontSize: 13,
              margin: '-8px 0 12px',
            }}
          >
            {blockWarnReason === 'out_of_range'
              ? playerKitDoor.get()?.numberBounds.boundsWarning(
                  blockWarn?.bounds,
                  language,
                )
              : blockWarnReason === 'invalid_email'
                ? getPublicQuizStrings(language).invalidEmailWarning
                : getPublicQuizStrings(language).requiredFieldWarning}
          </div>
        )}
      </Fragment>,
    );
  });

  // WEB-1025 / WEB-1107 / WEB-1794: системная запасная кнопка «Далее» —
  // когда экрану нечем продвинуться или авто-переход упёрся
  // (QuizFallbackCta.tsx).
  const ctaNode = renderQuizFallbackCta(
    visibleBlocks,
    autoAdvanceRescueVisible,
    screenLayout,
    isLast,
    screenHasUnfilledRequired,
    packNextButtonStyle,
    handleNextClick,
    chromeExtras,
    chromeKit,
    isEditorPreview,
    buttonStyle,
    textColor,
    language,
  );

  // Пачка настроек 2: прибитая шапка (QuizChromeFrame) и шапка вне
  // анимируемого корня экрана (`chrome.persistent`) — тогда она не затухает и
  // заливка прогресса едет плавно. Без новых полей пака разметка прежняя.
  const framedChrome = (
    <chromeKit.QuizChromeFrame pack={chromeExtras.pack} preview={isEditorPreview}>
      {chromeNode}
    </chromeKit.QuizChromeFrame>
  );
  const screenNode = (
    <>
    {chromeExtras.persistent ? framedChrome : null}
    {chromeExtras.pack?.chrome.logo === 'desktop-left' &&
    readQuizHeader(quizHeader).logoAlign === undefined ? (
      <chromeKit.DesktopLogo url={logoUrl} hidden={chromeExtras.visibility.hideLogo} />
    ) : null}
    {/* key по id экрана: без него React переиспользует узел и CSS-анимация
        появления (--screen-fade-ms из стиль-пака) не перезапускается. */}
    <div
      key={currentScreen.id}
      className="funnel-screen-in"
      data-testid="quiz-screen"
      data-screen-id={currentScreen.id}
      // Оси в data-атрибутах — по ним же цепляются правила страницы
      // (public-paywall.css): чтобы контенту было ЧТО распределять, предки
      // экрана обязаны отдать ему всю высоту.
      data-screen-align-y={screenLayout.alignY}
      data-screen-align-x={screenLayout.alignX}
      data-screen-fill={screenLayout.fill ? 'true' : undefined}
      style={screenRootLayoutStyle(screenLayout)}
      // WEB-1643: одна дверь сигнала «ввод завершён» для всех полей экрана —
      // focusout/keydown всплывают до корня экрана (см. handleScreenFocusOut).
      onBlur={handleScreenFocusOut}
      onKeyDown={handleScreenKeyDown}
    >
      {chromeExtras.persistent ? null : framedChrome}
      <ScreenContentColumn layout={screenLayout} testId="quiz-screen-content">
        {arrangeScreenContent(currentScreen.contentCard, currentScreen.blocks, blockNodes)}
        {ctaNode}
      </ScreenContentColumn>
    </div>
    </>
  );

  // WEB-927: полноэкранный фон охватывает ВЕСЬ экран — хром (прогресс, лого,
  // «назад», подпись раздела), блоки и CTA лежат поверх картинки внутри слоя
  // контента обёртки, поэтому остаются читаемыми (обёртка пиннит светлый текст
  // под вуалью).
  //
  // Квиз рисует ТОЛЬКО полноэкранный режим (appliesTo). Карточный фон здесь
  // намеренно не включаем: генерация давно кладёт background на hero-экраны
  // квиза, а квиз его никогда не рисовал — включение карточного режима задним
  // числом проявило бы картинки на уже опубликованных воронках. Это отдельное
  // продуктовое решение PM, не побочный эффект этого атома.
  return (
    // WEB-1012: блокам, которым мало своих полей, нужна воронка вокруг —
    // экран загрузки строит стадии из имён секций (тех же, что показывает
    // подпись раздела выше) и берёт цитаты из блоков отзывов этой воронки.
    // Провайдер стоит здесь, а не у каждого вызывающего: через этот визард
    // идут и публичная страница, и превью конструктора (шрам WEB-953).
    // WEB-2124/WEB-2164: дверь ухода наружу подаёт ТОЛЬКО плеер квиза —
    // рядом с провайдером содержимого воронки и по той же причине: уводящие
    // блоки («редирект», кнопка-ссылка) рисуются где угодно в дереве экрана, и
    // протаскивать обработчик пропсами через все слои значило бы менять
    // подписи четырёх компонентов ради двух типов блоков. Вне плеера (канва
    // конструктора, пейволл) двери нет — блок уводит сам и ничего не
    // засчитывает, выдуманного завершения квиза там не бывает.
    <ExternalExitProvider value={externalExitValue}>
      <FunnelContentProvider screens={screens} quizLoader={quizLoader}>
        <ScreenBackgroundWrapper
          background={currentScreen.background}
          stylePackId={stylePackId}
          appliesTo="full-screen"
        >
          {screenNode}
        </ScreenBackgroundWrapper>
      </FunnelContentProvider>
    </ExternalExitProvider>
  );
}
